import { CommonModule } from "@angular/common";
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  inject,
} from "@angular/core";
import {
  FormBuilder,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { cpfValidator, nameValidator } from "@shared/validatos";
import { NgxMaskDirective, provideNgxMask } from "ngx-mask";
import { DdiSelectComponent } from "../../../../shared/components/ddi-select/ddi-select.component";
import { countriesList } from "../../../../utils/constants/countries.constant";

export interface CartClientInfo {
  client_name: string;
  client_email: string;
  is_foreign_client: boolean;
  client_country: string;
  client_phone_country_code: string;
  client_phone: string;
  client_cpf: string;
  client_document: string;
  client_document_type: "cpf" | "passport";
}

@Component({
  selector: "app-cart-customer-info-section",
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NgxMaskDirective,
    DdiSelectComponent,
  ],
  providers: [provideNgxMask()],
  templateUrl: "./cart-customer-info-section.component.html",
  styleUrl: "./cart-customer-info-section.component.scss",
})
export class CartCustomerInfoSectionComponent implements OnChanges {
  @Input() highlightErrorsTrigger = 0;
  @Output() clientInfoChange = new EventEmitter<CartClientInfo>();

  private readonly formBuilder = inject(FormBuilder);
  readonly countriesList = countriesList;

  readonly clientInfoForm = this.formBuilder.group({
    is_foreign_client: [false],
    client_name: [
      "",
      [Validators.required, Validators.minLength(3), nameValidator],
    ],
    client_email: ["", [Validators.required, Validators.email]],
    client_country: ["BR", [Validators.required]],
    client_phone_country_code: ["+55", [Validators.required]],
    client_phone: ["", [Validators.required, Validators.minLength(10)]],
    client_cpf: ["", [Validators.required, cpfValidator]],
    client_document: [""],
    client_document_type: ["cpf" as "cpf" | "passport"],
  });

  constructor() {
    this.subscribeToForeignClientChange();
    this.emitClientInfo();
    this.clientInfoForm.valueChanges.subscribe(() => this.emitClientInfo());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["highlightErrorsTrigger"]?.currentValue > 0) {
      this.clientInfoForm.markAllAsTouched();
    }
  }

  hasError(controlName: keyof CartClientInfo): boolean {
    const control = this.clientInfoForm.get(controlName);
    return !!control && control.invalid && (control.touched || control.dirty);
  }

  private subscribeToForeignClientChange(): void {
    const {
      is_foreign_client,
      client_cpf,
      client_document,
      client_document_type,
      client_country,
      client_phone_country_code,
    } = this.clientInfoForm.controls;

    is_foreign_client.valueChanges.subscribe((isForeignClient) => {
      if (isForeignClient) {
        client_cpf.reset("", { emitEvent: false });
        client_cpf.clearValidators();
        client_cpf.disable({ emitEvent: false });
        client_document.addValidators(Validators.required);
        client_document.enable({ emitEvent: false });
        client_document_type.setValue("passport", { emitEvent: false });
        if (!client_country.value || client_country.value === "BR") {
          client_country.setValue("US", { emitEvent: false });
        }
        if (client_phone_country_code.value === "+55") {
          client_phone_country_code.setValue("+1", { emitEvent: false });
        }
      } else {
        client_document.reset("", { emitEvent: false });
        client_document.clearValidators();
        client_document.disable({ emitEvent: false });
        client_cpf.addValidators([Validators.required, cpfValidator]);
        client_cpf.enable({ emitEvent: false });
        client_document_type.setValue("cpf", { emitEvent: false });
        client_country.setValue("BR", { emitEvent: false });
        client_phone_country_code.setValue("+55", { emitEvent: false });
      }

      client_cpf.updateValueAndValidity({ emitEvent: false });
      client_document.updateValueAndValidity({ emitEvent: false });
      this.clientInfoForm.updateValueAndValidity();
    });
  }

  private emitClientInfo(): void {
    const rawValue = this.clientInfoForm.getRawValue();
    const isForeignClient = rawValue.is_foreign_client ?? false;

    this.clientInfoChange.emit({
      client_name: rawValue.client_name?.trim() ?? "",
      client_email: rawValue.client_email?.trim() ?? "",
      is_foreign_client: isForeignClient,
      client_country: rawValue.client_country ?? "BR",
      client_phone_country_code: rawValue.client_phone_country_code ?? "",
      client_phone: rawValue.client_phone ?? "",
      client_cpf: isForeignClient ? "" : rawValue.client_cpf ?? "",
      client_document: isForeignClient
        ? rawValue.client_document?.trim() ?? ""
        : "",
      client_document_type: isForeignClient ? "passport" : "cpf",
    });
  }
}
